import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';

import { cn } from '../lib/utils';
import { BackendAPI } from '../lib/api';
import { triggerEsxDownload } from '../lib/esxDownload';
import type { AdapterReport } from '../types';

interface DownloadEsxButtonProps {
  /** Persisted scope id. When present, export is direct. */
  scopeId?: string | null;
  /** Project id — required for save-then-export when scopeId is absent. */
  projectId?: string | null;
  /** Scope payload to persist if a save-then-export is needed. */
  scopeData?: object | null;
  /** Optional override for the ESX PROJECT_INFO/@name. */
  projectNameOverride?: string;
  /** Fired with the new scope id when a save-then-export persists the scope. */
  onSaved?: (scopeId: string) => void;
  /** Fired after a successful download with the adapter report. */
  onResult?: (filename: string, report: AdapterReport) => void;
  /** Fired with a human-readable message on any failure. */
  onError?: (message: string) => void;
  className?: string;
}

/**
 * "Download as ESX" action button.
 *
 * Direct export when a scopeId exists; otherwise save-then-export using
 * projectId + scopeData. Disabled with a tooltip when neither is available.
 */
export default function DownloadEsxButton({
  scopeId = null,
  projectId = null,
  scopeData = null,
  projectNameOverride,
  onSaved,
  onResult,
  onError,
  className,
}: DownloadEsxButtonProps) {
  const [busy, setBusy] = useState(false);

  const canSaveThenExport = Boolean(projectId && scopeData);
  const canExport = Boolean(scopeId) || canSaveThenExport;

  async function handleClick() {
    setBusy(true);
    try {
      let id = scopeId ?? null;

      if (!id && canSaveThenExport) {
        const saved = await BackendAPI.saveProjectScope(projectId as string, scopeData as object);
        id = (saved?.id as string | undefined) ?? null;
        if (!id) throw new Error('Saving the scope did not return an id.');
        onSaved?.(id);
      }

      if (!id) throw new Error('No scope available to export.');

      const result = await BackendAPI.downloadScopeEsx(id, projectNameOverride);
      triggerEsxDownload(result.filename, result.esx_b64);
      onResult?.(result.filename, result.report);
    } catch (err) {
      onError?.(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!canExport || busy}
      title={canExport ? 'Download an importable Xactimate .ESX file' : 'Save scope to a project to export'}
      className={cn(
        'inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
        canExport && !busy
          ? 'border-primary bg-primary text-white hover:bg-primary/90'
          : 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400',
        className
      )}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
      Download ESX
    </button>
  );
}
